import { AuthDialog } from "@/components/auth.dialog";
import { useUser } from "@/hooks/user";
import { isLiveBattleStatus, useLiveBattle } from "@/lib/backend/battle";
import { webPush } from "@/lib/push";
import { formatCurrency } from "@/lib/utils";
import { m } from "@/paraglide/messages";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@bidbattles/bloks/components/ui/accordion";
import { Button, ButtonLink } from "@bidbattles/bloks/components/ui/button";
import { BBBackendQueryOptions } from "@bidbattles/bloks/lib/rapace";
import { useSuspenseQuery } from "@tanstack/react-query";
import {
	ClientOnly,
	createFileRoute,
	useNavigate,
} from "@tanstack/react-router";
import { LucideLoader2 } from "lucide-react";
import { motion } from "motion/react";
import { Suspense, useEffect } from "react";
import { NotificationMessage, Star01 } from "untitledui-js/react";

const StarIcon = motion.create(Star01);
export const Route = createFileRoute("/battle/$id/details")({
	component: RouteComponent,
});

function RouteComponent() {
	const user = useUser();
	const { id } = Route.useParams();
	const query = useSuspenseQuery({
		...BBBackendQueryOptions.fetchBattle({ id }),
		refetchInterval: (query) => {
			const data = query.state.data;
			if (data?.status === "FINISHED" || data?.status === "STOPPED") {
				return false;
			}

			return 1000;
		},
		refetchIntervalInBackground: true,
	});
	const navigate = useNavigate();
	const liveBattle = useLiveBattle(id, { connectIfNotLive: false });

	const battle = query.data;
	const totalSeats = String(battle.total_seats);
	const mayJoinBattle =
		battle.reserved &&
		liveBattle.state !== "evicted" &&
		liveBattle.state !== "disconnected";
	const isWinner =
		battle.status === "FINISHED" &&
		battle.reserved &&
		battle.winner_details?.id === user?.id;

	useEffect(() => {
		if (!isLiveBattleStatus(battle.status) || liveBattle.state === "connecting")
			return;

		if (mayJoinBattle) {
			navigate({
				from: Route.fullPath,
				to: "/battle/$id/battle",
				params: { id },
			});
		}
	}, [battle.status, id, navigate, liveBattle.state, mayJoinBattle]);

	function renderCTA() {
		if (battle.status === "STOPPED") {
			return (
				<div className="text-gray-500 text-center gap-2">
					<p>
						This battle was cancelled. Our support team will reach out to
						affected players with further information.
					</p>
				</div>
			);
		}

		if (battle.status === "FINISHED") {
			if (isWinner) {
				return (
					<motion.div
						initial={{ scaleX: 0 }}
						animate={{ scaleX: 1 }}
						className="relative rounded-2xl border border-accent-primary justify-center items-center p-box flex flex-col text-center"
					>
						<StarIcon
							initial={{
								opacity: 0,
								translateX: "1rem",
								top: "-0.75rem",
								left: "-0.75rem",
								rotate: "0deg",
							}}
							animate={{
								opacity: 1,
								translateX: 0,
								rotate: "45deg",
								transition: { delay: 0.35 },
							}}
							size="2rem"
							fill="currentColor"
							className="absolute text-accent-primary"
						/>
						<StarIcon
							initial={{
								opacity: 0,
								translateX: "1rem",
								bottom: "-0.75rem",
								left: "-0.75rem",
								rotate: "0deg",
							}}
							animate={{
								opacity: 1,
								translateX: 0,
								rotate: "45deg",
								transition: { delay: 0.35 },
							}}
							size="2rem"
							fill="currentColor"
							className="absolute text-accent-primary"
						/>
						<StarIcon
							initial={{
								opacity: 0,
								translateX: "-1rem",
								top: "-0.75rem",
								right: "-0.75rem",
								rotate: "0deg",
							}}
							animate={{
								opacity: 1,
								translateX: 0,
								rotate: "-45deg",
								transition: { delay: 0.35 },
							}}
							size="2rem"
							fill="currentColor"
							className="absolute text-accent-primary"
						/>
						<StarIcon
							initial={{
								opacity: 0,
								translateX: "-1rem",
								bottom: "-0.75rem",
								right: "-0.75rem",
								rotate: "0deg",
							}}
							animate={{
								opacity: 1,
								translateX: 0,
								rotate: "-45deg",
								transition: { delay: 0.35 },
							}}
							size="2rem"
							fill="currentColor"
							className="absolute text-accent-primary"
						/>
						<motion.p
							initial={{ opacity: 0, translateY: "25%" }}
							animate={{
								opacity: 1,
								translateY: 0,
								transition: { delay: 0.35 },
							}}
							className="font-bold text-2xl"
						>
							You won!
						</motion.p>
						<motion.p
							initial={{ opacity: 0, translateY: "25%" }}
							animate={{
								opacity: 1,
								translateY: 0,
								transition: { delay: 0.35 },
							}}
							className="text-sm text-gray-500"
						>
							Our team will be in touch regarding how to claim your prize.
						</motion.p>
					</motion.div>
				);
			}

			return (
				<div className="text-gray-500 text-center gap-2">
					{!!battle.winner_details && (
						<p>
							<strong>{battle.winner_details.name}</strong> won this battle.
						</p>
					)}
					{battle.reserved && <p>Better luck next time!</p>}
				</div>
			);
		}

		if (!user) {
			return null;
		}

		if (!battle.reserved) {
			if (battle.open_seats === 0) {
				return <Button disabled>Battle is full</Button>;
			}

			return (
				<ButtonLink
					from={Route.fullPath}
					to="/battle/$id/bids"
					params={{ id }}
					search={{ rebuy: false }}
				>
					Enter battle
				</ButtonLink>
			);
		}

		if (battle.status === "FULL" || battle.status === "OPEN") {
			return (
				<div className="flex flex-col gap-4">
					<p className="text-gray-900 text-center">
						Battle starts when all seats are filled.
					</p>
					<ClientOnly>
						<Suspense fallback={null}>
							<EnablePNCTA />
						</Suspense>
					</ClientOnly>
				</div>
			);
		}

		if (liveBattle.state === "connecting") {
			return (
				<div className="flex justify-center items-center">
					<LucideLoader2 className="animate-spin" />
				</div>
			);
		}

		return (
			<div className="w-full flex flex-col justify-center items-stretch px-box gap-2 text-center">
				{mayJoinBattle ? (
					<ButtonLink
						from={Route.fullPath}
						to="/battle/$id/battle"
						params={{ id }}
					>
						Join now!
					</ButtonLink>
				) : (
					<p className="text-sm text-gray-500">
						You've been evicted and cannot rejoin.
					</p>
				)}
			</div>
		);
	}
	return (
		<div className="p-box flex flex-col gap-8 col-span-4">
			<div className="flex justify-between">
				<div>
					<p className="text-xs text-gray-500 leading-[1.125rem]">
						{m.bidbattle__open_seats()}
					</p>
					<p className="md:text-3xl md:leading-[2.375rem]">
						<span className="font-bold">
							{String(battle.total_seats - battle.open_seats).padStart(
								totalSeats.length,
								"0",
							)}
						</span>
						/{totalSeats}
					</p>
				</div>
				<div>
					<p className="text-xs text-gray-500 leading-[1.125rem]">
						{m.bidbattle__entry_price()}
					</p>
					<p className="md:text-3xl md:leading-[2.375rem] font-bold">
						{formatCurrency("EUR", battle.entry_price)}
					</p>
				</div>
			</div>
			<AuthDialog
				trigger={<Button>Login to Enter Battle</Button>}
				hidden={
					!!user || battle.status === "FINISHED" || battle.status === "STOPPED"
				}
			/>
			{renderCTA()}
			<Accordion>
				<AccordionItem value="entries">
					<AccordionTrigger>
						<span>
							Entries&nbsp;
							<span className="font-bold">{battle.reservations.length}</span>
						</span>
					</AccordionTrigger>
					<AccordionContent>
						<ol className="list-none flex flex-col gap-4">
							{[...battle.reservations].reverse().map((resvervation, index) => {
								const padCount = String(battle.reservations.length).length;
								const id = battle.reservations.length - index;
								return (
									<li key={resvervation} className="flex gap-2">
										<p className="font-bold min-w-4">
											{String(id).padStart(padCount)}
										</p>
										<p>{resvervation}</p>
									</li>
								);
							})}
						</ol>
					</AccordionContent>
				</AccordionItem>
				<AccordionItem value="how-it-works">
					<AccordionTrigger onClick={() => navigate({ to: "/how-it-works" })}>
						How it works
					</AccordionTrigger>
				</AccordionItem>
			</Accordion>
		</div>
	);
}

function EnablePNCTA() {
	const pns = webPush.usePermission();
	if (!pns.supported) {
		return null;
	}

	if (!pns.subscription) {
		return (
			<Button
				variant="outline"
				disabled={pns.enabling}
				onClick={() => pns.enable()}
			>
				{pns.enabling ? (
					<LucideLoader2 className="animate-spin" />
				) : (
					<>
						<NotificationMessage stroke="currentColor" size="1em" />
						Notify me on this device
					</>
				)}
			</Button>
		);
	}

	return (
		<p className="text-gray-900 text-center">
			You'll get a notification on this device when the battle starts.
		</p>
	);
}
